/**
 * Analytics dashboard card → permission mapping.
 * Dedicated permissions (register in backend/RBAC); not shared with Reports or other modules.
 *
 * Page access (sidebar): dashboard.analytics_card — see HomePage.jsx
 */

export const ANALYTICS_CARD_IDS = {
  SITE: 'site',
  EMPLOYEE_OVERVIEW: 'employeeOverview',
  EMPLOYEE_ATTENDANCE: 'employeeAttendance',
  RIDES: 'rides',
  INVENTORY: 'inventory',
  TASKS_ISSUES: 'tasksIssues'
};

/** @type {Record<string, string>} */
export const ANALYTICS_CARD_PERMISSIONS = {
  [ANALYTICS_CARD_IDS.SITE]: 'dashboard.analytics.site_card',
  [ANALYTICS_CARD_IDS.EMPLOYEE_OVERVIEW]: 'dashboard.analytics.employee_overview_card',
  [ANALYTICS_CARD_IDS.EMPLOYEE_ATTENDANCE]: 'dashboard.analytics.employee_attendance_card',
  [ANALYTICS_CARD_IDS.RIDES]: 'dashboard.analytics.rides_card',
  [ANALYTICS_CARD_IDS.INVENTORY]: 'dashboard.analytics.inventory_card',
  [ANALYTICS_CARD_IDS.TASKS_ISSUES]: 'dashboard.analytics.tasks_issues_card'
};

/** All per-card permission strings (for sidebar module gates, docs, backend seeding). */
export const ANALYTICS_ALL_CARD_PERMISSIONS = Object.values(ANALYTICS_CARD_PERMISSIONS);

export const ANALYTICS_PAGE_PERMISSION = 'dashboard.analytics_card';

export const hasAnyPermission = (userPermissions, required) => {
  if (!Array.isArray(userPermissions) || userPermissions.length === 0) {
    return false;
  }
  if (!required) {
    return true;
  }
  if (Array.isArray(required)) {
    return required.some(perm => userPermissions.includes(perm));
  }
  return userPermissions.includes(required);
};

export const getVisibleAnalyticsCards = userPermissions =>
  Object.values(ANALYTICS_CARD_IDS).reduce((visible, cardId) => {
    visible[cardId] = hasAnyPermission(
      userPermissions,
      ANALYTICS_CARD_PERMISSIONS[cardId]
    );
    return visible;
  }, {});

const countRow1Cards = visible => {
  let count = 0;
  if (visible.site) count += 1;
  if (visible.employeeOverview) count += 1;
  if (visible.employeeAttendance) count += 1;
  if (visible.rides) count += 1;
  return count;
};

/**
 * Resolves responsive grid classes and column grouping for the analytics layout.
 */
export const resolveAnalyticsLayout = visible => {
  const row1Count = countRow1Cards(visible);
  const hasSite = visible.site;
  const hasOverview = visible.employeeOverview;
  const hasAttendance = visible.employeeAttendance;
  const hasRides = visible.rides;
  const hasInventory = visible.inventory;
  const hasTasksIssues = visible.tasksIssues;

  const hasExactlyOneThirdCard =
    (hasAttendance || hasRides) && hasAttendance !== hasRides;

  // Site + Employee stacked | Attendance or Rides featured (full-width column)
  const useStackedPairFeaturedLayout =
    row1Count === 3 &&
    hasSite &&
    hasOverview &&
    hasExactlyOneThirdCard;

  const stackSiteAndOverview =
    hasSite &&
    hasOverview &&
    (hasAttendance || hasRides) &&
    !useStackedPairFeaturedLayout;

  const showRow1 = row1Count > 0;
  const showRow2 = hasInventory || hasTasksIssues;

  let snapshotGridClass = 'analytics-snapshot-grid';

  if (useStackedPairFeaturedLayout) {
    snapshotGridClass += ' analytics-snapshot-grid--pair-featured';
  } else if (row1Count === 1) {
    snapshotGridClass += ' analytics-snapshot-grid--single';
  } else if (row1Count === 2) {
    snapshotGridClass += ' analytics-snapshot-grid--duo';
  } else if (row1Count === 3) {
    snapshotGridClass += ' analytics-snapshot-grid--trio';
  } else if (row1Count >= 4) {
    snapshotGridClass += ' analytics-snapshot-grid--quad';
  }

  let row2GridClass = 'grid grid-cols-1 gap-4 md:gap-6';

  if (hasInventory && hasTasksIssues) {
    row2GridClass += ' lg:grid-cols-[minmax(0,_2fr)_minmax(0,_3fr)]';
  } else if (hasInventory || hasTasksIssues) {
    row2GridClass += ' max-w-4xl mx-auto w-full';
  }

  return {
    showRow1,
    showRow2,
    snapshotGridClass,
    row2GridClass,
    stackSiteAndOverview,
    useStackedPairFeaturedLayout,
    row1Count,
    visibleCardCount:
      row1Count + (hasInventory ? 1 : 0) + (hasTasksIssues ? 1 : 0)
  };
};
